import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { COMPANY, STATS, MANIFESTO } from "../data/content";
import { Reveal } from "../lib/motion";
import PageHero from "../components/PageHero";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline={`Established ${COMPANY.since}`}
        title="About us"
        subtitle="Sundrop Infotech Pvt Ltd is an integrated facility management and manpower company built on a simple belief - mechanised, process-driven services deliver cleaner, safer, more reliable outcomes."
      />

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-none">
              Machines, method <br /> and manpower.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base md:text-lg text-neutral-400 leading-relaxed">
              <p>
                Since {COMPANY.since}, Sundrop has partnered with organisations across education,
                corporate, industrial, healthcare, retail and hospitality to run the services
                that keep their premises spotless, secure and fully staffed.
              </p>
              <p>
                We differentiate through mechanisation - auto-scrubbers, modern equipment and
                defined SOPs - combined with trained, supervised teams. The result is service
                that is consistent and measurable, not dependent on any single day&apos;s effort.
              </p>
              <p>
                Today we operate across {COMPANY.cities.join(", ")}, delivering everything from
                NABH-aligned hospital hygiene to industrial workforce deployment under one
                accountable partnership.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.06} className="bg-[#0a0a0a] px-6 py-10">
              <p className="font-display text-4xl md:text-5xl font-black text-accent">{s.value}</p>
              <p className="mt-2 text-sm text-neutral-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-accent">Our principles</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tight leading-none">
              How we operate.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-2">
            {MANIFESTO.map((m) => (
              <Reveal key={m.num} className="bg-[#0a0a0a] p-8 md:p-10">
                <span className="font-display text-5xl font-black text-white/10">{m.num}</span>
                <h3 className="font-display mt-4 text-2xl font-bold tracking-tight">{m.title}</h3>
                <p className="mt-3 text-neutral-400 leading-relaxed">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <Reveal className="flex flex-col items-start gap-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-10 md:flex-row md:items-center md:justify-between md:p-14">
          <div className="flex gap-4">
            <MapPin className="mt-1 shrink-0 text-accent" size={22} />
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Head Office - Pune</h3>
              <p className="mt-2 max-w-md text-neutral-400 leading-relaxed">{COMPANY.address}</p>
            </div>
          </div>
          <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-bold text-black transition-transform duration-200 hover:scale-[0.97]">
            Contact us <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
